import React from 'react';
import {
    Button,
    Box,
    makeStyles,
    Typography,
    useTheme,
    alpha,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { TEmailMarketingPlan } from '../constants/types';
import numeral from 'numeral';
import { Check } from 'react-feather';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 5,
        padding: theme.spacing(5),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(5),
        cursor: 'pointer',
        position: 'relative',
        transition:
            'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease, background-color 0.25s ease',
        '&:hover': {
            transform: 'translateY(-5px)',
        },
        overflow: 'hidden',
    },
    dollarsContent: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    dollars: {
        fontSize: 56,
        fontWeight: 700,
    },
    pricing: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    features: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    featureItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    isRecommended: {
        border: `1px solid ${theme.palette.primary.main}`,
        top: -20,
        boxShadow: theme.shadows[6],
    },
    title: {
        fontWeight: 700,
        zIndex: 2,
    },
    button: {
        height: 75,
    },
    image: {
        alignSelf: 'center',
        maxWidth: 'auto',
        height: 120,
    },
    badge: {
        position: 'absolute',
        backgroundColor: alpha(theme.palette.secondary.main, 1),
        color: theme.palette.getContrastText(theme.palette.primary.main),
        padding: 20,
        width: 350,
        transform: 'translateY(-10%) translateX(50%) rotate(45deg) ',
    },
    badgeText: {
        textTransform: 'uppercase',
        letterSpacing: 2,
    },
}));

/**
 * Represents an email marketing view.
 *
 * @return {*}
 */
export const EmailMarketingPlan = ({ plan }: { plan: TEmailMarketingPlan }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const theme = useTheme();

    const { isRecommended, name, prices, features, Image } = plan;

    return (
        <Box
            className={clsx({
                [classes.root]: true,
                [classes.isRecommended]: isRecommended,
            })}
        >
            {isRecommended && (
                <Box className={classes.badge}>
                    <Typography
                        variant="h5"
                        align="center"
                        className={classes.badgeText}
                    >
                        {t('Plans.Recommended')}
                    </Typography>
                </Box>
            )}
            <Typography variant="h1" align="center" className={classes.title}>
                {name}
            </Typography>
            <Image className={classes.image} />
            <Box className={classes.pricing}>
                <Box className={classes.dollarsContent}>
                    <Typography variant="h1">
                        <sup>$</sup>
                    </Typography>
                    <Typography
                        variant="h1"
                        align="center"
                        className={classes.dollars}
                    >
                        {numeral(prices.dollars).format('0,0')}
                    </Typography>
                    <Typography variant="h3">
                        <sub>/mo</sub>
                    </Typography>
                </Box>
                <Typography variant="h4" color="primary">
                    {numeral(prices.pesos).format('0,0')} MXN
                </Typography>
            </Box>
            <Box className={classes.features}>
                {features.map((feature, index) => (
                    <Box key={index} className={classes.featureItem}>
                        <Check color={theme.palette.secondary.main} />
                        <Typography variant="body1">
                            {t(`Plans.${feature}`)}
                        </Typography>
                    </Box>
                ))}
            </Box>
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="flex-end"
                flexGrow={1}
            >
                <Button
                    variant="contained"
                    color="primary"
                    className={classes.button}
                    href={`https://api.whatsapp.com/send?phone=5217771080599&text=Buen%20día%2C%20estoy%20interesado%20en%20Plan%20${name}%20de%20Email%20Marketing%2C%20¿Podrías%20darme%20más%20información%3F`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {t('Plans.ContactUs')}
                </Button>
            </Box>
        </Box>
    );
};
